import { IconAlertCircle, IconChevronLeft, IconChevronRight, IconSearch } from '@myplayer/ui';
import { useMemo, useState, type ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth-context.js';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
} from '../components/ui/primitives.js';
import { HELP_ARTICLES } from './help-registry.js';
import {
  HELP_CATEGORIES,
  filterHelpArticles,
  getHelpCategoryMeta,
  type HelpArticle,
  type HelpCategoryId,
} from './help-types.js';
import { MarkdownRenderer } from './markdown-renderer.js';

export interface HelpPageProps {
  readonly articles?: readonly HelpArticle[] | undefined;
  readonly initialSlug?: string | undefined;
}

export function HelpPage({ articles = HELP_ARTICLES, initialSlug }: HelpPageProps): ReactElement {
  const { session } = useAuth();
  const params = useParams<{ slug?: string }>();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<HelpCategoryId | 'all'>('all');
  const [localSlug, setLocalSlug] = useState<string | undefined>(initialSlug);

  const activeSlug = params.slug ?? localSlug ?? articles[0]?.slug;

  const filteredArticles = useMemo(
    () => filterHelpArticles(articles, searchQuery, selectedCategory),
    [articles, searchQuery, selectedCategory],
  );

  const selectedArticle = useMemo(() => {
    if (filteredArticles.length === 0) {
      return undefined;
    }
    const exactMatch = filteredArticles.find((article) => article.slug === activeSlug);
    return exactMatch ?? filteredArticles[0];
  }, [filteredArticles, activeSlug]);

  const selectedIndex = useMemo(() => {
    if (!selectedArticle) {
      return -1;
    }
    return filteredArticles.findIndex((item) => item.slug === selectedArticle.slug);
  }, [filteredArticles, selectedArticle]);

  const previousArticle = selectedIndex > 0 ? filteredArticles[selectedIndex - 1] : undefined;
  const nextArticle =
    selectedIndex >= 0 && selectedIndex < filteredArticles.length - 1
      ? filteredArticles[selectedIndex + 1]
      : undefined;

  const handleSelectArticle = (slug: string): void => {
    setLocalSlug(slug);
    navigate(`/help/${slug}`);
  };

  const handleResetFilters = (): void => {
    setSearchQuery('');
    setSelectedCategory('all');
  };

  return (
    <div data-testid="help-centre-page" className="space-y-6">
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-surface-muted text-fg-secondary">
                  <IconAlertCircle />
                </span>
                <CardTitle className="text-[20px]">Help Centre</CardTitle>
              </div>
              <CardDescription>
                Plain-English guides for managing GS1 Digital Links, conditional routing, product
                recalls, permissions, and compliance.
              </CardDescription>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" data-testid="help-readonly-badge">
                Available to all roles (Read-only)
              </Badge>
              {session !== null && (
                <Badge variant="outline" data-testid="help-role-badge">
                  Viewing as {session.personaRole}
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <label htmlFor="help-search-input" className="sr-only">
                Search help articles
              </label>
              <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-fg-muted">
                <IconSearch />
              </span>
              <Input
                id="help-search-input"
                type="search"
                aria-label="Search help articles"
                data-testid="help-search-input"
                placeholder="Search help pages by title or topic (e.g. GTIN, recall, 307 redirect, roles)..."
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="pl-9"
              />
            </div>

            {searchQuery.trim().length > 0 && (
              <Button
                variant="secondary"
                size="sm"
                data-testid="help-search-clear"
                onClick={() => setSearchQuery('')}
              >
                Clear search
              </Button>
            )}
          </div>

          <div
            role="group"
            aria-label="Filter help topics by category"
            className="flex flex-wrap items-center gap-2"
          >
            <Button
              variant={selectedCategory === 'all' ? 'primary' : 'secondary'}
              size="sm"
              data-testid="help-category-all"
              onClick={() => setSelectedCategory('all')}
            >
              All Topics ({String(filterHelpArticles(articles, searchQuery, 'all').length)})
            </Button>
            {HELP_CATEGORIES.map((category) => {
              const count = filterHelpArticles(articles, searchQuery, category.id).length;
              return (
                <Button
                  key={category.id}
                  variant={selectedCategory === category.id ? 'primary' : 'secondary'}
                  size="sm"
                  data-testid={`help-category-${category.id}`}
                  onClick={() => setSelectedCategory(category.id)}
                >
                  {category.label} ({String(count)})
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {filteredArticles.length === 0 ? (
        <Card data-testid="help-search-empty">
          <CardContent className="py-10 text-center">
            <p className="text-[15px] font-semibold text-fg">
              No help pages match &ldquo;{searchQuery.trim()}&rdquo;
            </p>
            <p className="mt-1 text-[13px] text-fg-secondary">
              Try searching for a broader term such as GTIN, workspace, recall, or permissions.
            </p>
            <div className="mt-4">
              <Button variant="secondary" size="sm" onClick={handleResetFilters}>
                Show all help pages
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <nav
            aria-label="Help Centre articles"
            data-testid="help-article-nav"
            className="space-y-2 lg:col-span-4"
          >
            <div className="px-1 text-[12px] font-semibold text-fg-muted">
              Showing {String(filteredArticles.length)} of {String(articles.length)} pages
            </div>

            {filteredArticles.map((article) => {
              const isSelected = selectedArticle?.slug === article.slug;
              const categoryMeta = getHelpCategoryMeta(article.category);

              return (
                <Button
                  key={article.slug}
                  type="button"
                  variant="ghost"
                  size="inline"
                  data-testid={`help-nav-item-${article.slug}`}
                  aria-current={isSelected ? 'page' : undefined}
                  onClick={() => handleSelectArticle(article.slug)}
                  className={`block w-full whitespace-normal rounded-card border p-3.5 text-left leading-normal transition-colors ${
                    isSelected
                      ? 'border-brand bg-brand-wash text-fg'
                      : 'border-border bg-surface text-fg hover:border-brand hover:bg-brand-wash-hover'
                  }`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-1.5">
                    <span className="text-[14px] font-semibold text-fg">{article.title}</span>
                    {article.badge !== undefined && (
                      <Badge
                        variant={article.badge === 'PLACEHOLDER' ? 'warning' : 'accent'}
                        data-testid={`help-badge-${article.slug}`}
                      >
                        {article.badge}
                      </Badge>
                    )}
                  </div>
                  <p className="mt-1 line-clamp-2 text-[12px] text-fg-secondary">
                    {article.summary}
                  </p>
                  <div className="mt-2 flex items-center justify-between text-[11px] text-fg-muted">
                    <span>{categoryMeta.label}</span>
                    <span>{String(article.readTimeMinutes)} min read</span>
                  </div>
                </Button>
              );
            })}
          </nav>

          {selectedArticle !== undefined && (
            <Card data-testid="help-article-content" className="lg:col-span-8">
              <CardHeader className="block border-b border-subtle pb-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Badge variant="secondary">
                    {getHelpCategoryMeta(selectedArticle.category).label}
                  </Badge>
                  <div className="flex items-center gap-2">
                    {selectedArticle.badge !== undefined && (
                      <Badge
                        variant={selectedArticle.badge === 'PLACEHOLDER' ? 'warning' : 'accent'}
                      >
                        {selectedArticle.badge}
                      </Badge>
                    )}
                    <span className="text-[12px] text-fg-muted">
                      {String(selectedArticle.readTimeMinutes)} min read
                    </span>
                  </div>
                </div>
                <CardTitle
                  data-testid="help-active-article-title"
                  className="mt-2 text-[22px] font-bold"
                >
                  {selectedArticle.title}
                </CardTitle>
                <CardDescription className="text-[14px]">{selectedArticle.summary}</CardDescription>
              </CardHeader>

              <CardContent className="pt-5">
                <MarkdownRenderer
                  markdown={selectedArticle.markdown}
                  hideLeadingTitle={selectedArticle.title}
                />

                <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-subtle pt-4">
                  {previousArticle !== undefined ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      data-testid="help-prev-article"
                      onClick={() => handleSelectArticle(previousArticle.slug)}
                    >
                      <IconChevronLeft />
                      <span>Previous: {previousArticle.title}</span>
                    </Button>
                  ) : (
                    <span />
                  )}

                  {nextArticle !== undefined ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      data-testid="help-next-article"
                      onClick={() => handleSelectArticle(nextArticle.slug)}
                    >
                      <span>Next: {nextArticle.title}</span>
                      <IconChevronRight />
                    </Button>
                  ) : (
                    <span />
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
